import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useModalContext } from '@/context/modal';
import Icon, { Icons } from '@/components/elements/Icon';
import { FloatingOverlay } from '@floating-ui/react';
import { useTransition, animated, easings } from '@react-spring/web';
import useOnClickOutside from '@/presentation/hooks/useOnClickOutside';
import useOnKeypress from '@/presentation/hooks/useOnKeypress';

const sizeMap = {
  xs: 'max-w-xs',
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
};

const justifyMap = {
  top: 'justify-start',
  center: 'justify-center',
  bottom: 'justify-end',
};

const ANIMATION_TIME = 350;

export const Modal = ({
  children,
  size = 'sm',
  clickOffToClose = false,
  showCloseButton = true,
  offset,
  modalKey,
  justify = 'top',
  onClose,
}: {
  children?: ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  clickOffToClose?: boolean;
  showCloseButton?: boolean;
  offset?: string;
  justify?: 'top' | 'center' | 'bottom';
  modalKey?: string;
  onClose?: () => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);
  const { setModal, closeModal: closeModalContext } = useModalContext();
  const sizeClass = sizeMap[size];

  const closeModal = useCallback(() => {
    setShow(false);
    setTimeout(() => {
      setModal(null);
      if (typeof onClose === 'function') {
        onClose();
      }
    }, ANIMATION_TIME);
    closeModalContext(modalKey);
  }, [modalKey, closeModalContext, setModal, onClose]);

  const clickOutsideHandler = useCallback(
    () => clickOffToClose && closeModal(),
    [clickOffToClose, closeModal]
  );

  const closeOnEsc = useCallback(closeModal, [closeModal]);

  useOnKeypress('Escape', closeOnEsc, show);
  useOnClickOutside(ref, clickOutsideHandler);

  useEffect(() => {
    setShow(true);
  }, []);

  const transitions = useTransition(show, {
    from: {
      opacity: 0.5,
      transform: 'translateY(50%)',
    },
    enter: {
      opacity: 1,
      transform: 'translateY(0%)',
    },
    config: {
      duration: ANIMATION_TIME,
      // bounce: 100,
      easing: easings.easeOutCubic,
    },
  });

  return transitions(
    (style, show) =>
      show && (
        <FloatingOverlay
          className={`bg-gray-800 bg-opacity-50 flex flex-col ${justifyMap[justify]} items-center z-20 px-4 w-full hide-scroll`}
          lockScroll={true}
        >
          <animated.div
            style={style}
            ref={ref}
            className={`${sizeClass} ${offset} w-full my-10 relative max-h-screen bg-gray-100 rounded-2xl overflow-y-scroll hide-scroll shadow-xl`}
          >
            {showCloseButton ? (
              <button
                onClick={closeModal}
                aria-label="close"
                className="absolute right-2 top-2 p-2 rounded-full z-10"
              >
                <Icon className="text-gray-500" name={Icons.X_MARK} size={20} />
              </button>
            ) : null}
            <div className="hide-scroll max-h-full overflow-x-visible overflow-y-auto">
              {children}
            </div>
          </animated.div>
        </FloatingOverlay>
      )
  );
};

export default Modal;
